import React, { useRef, useEffect, useState, useCallback } from 'react';

/**
 * VideoBackground Component - Optimized for fast page load
 * 
 * Supports three background types:
 * - video: Dynamic video background
 * - gradient: CSS gradient background
 * - image: Static image background
 * 
 * Load sequence for video:
 * 0ms   - Poster image loads (via CSS background)
 * 400ms - Video element added to DOM (preload="none")
 * 2000ms - Video starts playing
 */

// Gradient style presets
const GRADIENT_STYLES = {
  ocean: 'from-blue-600 via-cyan-500 to-teal-400',
  sunset: 'from-orange-500 via-pink-500 to-purple-600',
  forest: 'from-green-600 via-emerald-500 to-teal-400',
  royal: 'from-indigo-600 via-purple-500 to-pink-400',
  midnight: 'from-slate-900 via-purple-900 to-slate-800',
  warmth: 'from-amber-500 via-orange-400 to-yellow-300',
  arctic: 'from-cyan-400 via-blue-300 to-indigo-400',
  neutral: 'from-white via-gray-100 to-gray-300',
  custom: 'from-primary via-accent to-secondary'
};

const VideoBackground = ({ 
  videoSrcWebm = '/videos/beach-waves.webm',
  videoSrcMp4 = '/videos/beach-waves.mp4',
  posterSrc = '/videos/beach-poster.jpg',
  imageUrl = '',  // New: separate image URL for image background type
  overlayOpacity = 0.3,
  textColor = 'white',
  className = '',
  // New props for background type support
  backgroundType = 'video',  // 'video' | 'gradient' | 'image' | 'color'
  gradientStyle = 'ocean',   // preset gradient style
  solidColor = '#081D33',    // solid background color
  enabled = true,
  children 
}) => {
  const videoRef = useRef(null);
  const hasInitialized = useRef(false);
  
  // All states start false - video is NOT in DOM initially
  const [videoState, setVideoState] = useState({
    shouldRender: false,    // Video element in DOM?
    shouldPlay: false,      // Should video be playing?
    isLoaded: false,        // Has video loaded enough to show?
    reducedMotion: false    // User prefers reduced motion?
  });

  // Check reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      setVideoState(s => ({ ...s, reducedMotion: true }));
    }
    
    const handler = (e) => setVideoState(s => ({ ...s, reducedMotion: e.matches }));
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Delayed video initialization - only for video type
  useEffect(() => {
    if (hasInitialized.current || videoState.reducedMotion || backgroundType !== 'video') return;
    hasInitialized.current = true;

    // Step 1: Add video to DOM after 400ms
    const renderTimer = setTimeout(() => {
      setVideoState(s => ({ ...s, shouldRender: true }));
    }, 400);

    // Step 2: Start playing after 2000ms
    const playTimer = setTimeout(() => {
      setVideoState(s => ({ ...s, shouldPlay: true }));
    }, 2000);

    return () => {
      clearTimeout(renderTimer);
      clearTimeout(playTimer);
    };
  }, [videoState.reducedMotion, backgroundType]);

  // Handle video playback
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoState.shouldPlay) return;

    video.play().catch(() => {
      // Autoplay blocked - poster continues showing
    });
  }, [videoState.shouldPlay]);

  // Video loaded callback
  const handleVideoLoaded = useCallback(() => {
    setVideoState(s => ({ ...s, isLoaded: true }));
  }, []);

  // Text color class
  const textColorClass = textColor === 'blue' 
    ? 'video-bg-text-blue' 
    : textColor === 'dark' 
      ? 'video-bg-text-dark' 
      : 'video-bg-text-white';

  const showVideo = !videoState.reducedMotion && backgroundType === 'video' && enabled;
  const videoVisible = showVideo && videoState.isLoaded;
  const showGradient = backgroundType === 'gradient' && enabled;
  const showImage = backgroundType === 'image' && enabled;
  const showColor = backgroundType === 'color' && enabled;
  const showFallback = !enabled;  // Show fallback when disabled

  // Determine which image to show
  // Handle API paths - convert /api/... to full URL using window.location.origin
  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) {
      // Use process.env or window.location for API media URLs
      const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      return `${baseUrl}${url}`;
    }
    return url;
  };
  const backgroundImageUrl = showImage && imageUrl ? resolveImageUrl(imageUrl) : posterSrc;

  // Get gradient class
  const gradientClass = GRADIENT_STYLES[gradientStyle] || GRADIENT_STYLES.ocean;

  return (
    <div 
      className={`relative ${className}`}
      style={{ 
        isolation: 'isolate',
        contain: 'layout style paint'
      }}
    >
      {/* Background Container */}
      <div 
        className="absolute inset-0 overflow-hidden"
        style={{
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden'
        }}
      >
        {/* Gradient Background */}
        {showGradient && (
          <div 
            className={`absolute inset-0 bg-gradient-to-br ${gradientClass}`}
            style={{ transform: 'translateZ(0)' }}
          />
        )}

        {/* Solid Color Background */}
        {showColor && (
          <div 
            className="absolute inset-0"
            style={{ backgroundColor: solidColor, transform: 'translateZ(0)' }}
          />
        )}

        {/* Image/Poster Background - Shows for video (as poster) or image type */}
        {(showVideo || showImage || showFallback) && (
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: `url(${backgroundImageUrl})`,
              opacity: videoVisible ? 0 : 1,
              transition: 'opacity 0.8s ease-out',
              transform: 'translateZ(0)'
            }}
          />
        )}

        {/* Video - Only in DOM after 400ms delay and for video type */}
        {showVideo && videoState.shouldRender && (
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            preload="none"
            onCanPlayThrough={handleVideoLoaded}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: videoVisible ? 1 : 0,
              transition: 'opacity 0.8s ease-out',
              transform: 'translate3d(0, 0, 0)',
              backfaceVisibility: 'hidden'
            }}
          >
            <source src={videoSrcWebm} type="video/webm" />
            <source src={videoSrcMp4} type="video/mp4" />
          </video>
        )}

        {/* Overlay */}
        <div 
          className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/40"
          style={{ 
            opacity: overlayOpacity * 2,
            transform: 'translateZ(0)'
          }}
        />
      </div>

      {/* Content */}
      <div className={`relative z-10 ${textColorClass}`}>
        {children}
      </div>
    </div>
  );
};

/**
 * GradientBackground Component
 * Lightweight alternative - no video loading
 */
export const GradientBackground = ({
  gradientSrc = '/videos/ocean-gradient.jpg',
  overlayOpacity = 0.6,
  solidColor = null,
  className = '',
  children
}) => {
  return (
    <div 
      className={`relative overflow-hidden ${className}`}
      style={{ contain: 'layout style paint' }}
    >
      {solidColor ? (
        <div 
          className="absolute inset-0"
          style={{ backgroundColor: solidColor, transform: 'translateZ(0)' }}
        />
      ) : (
        <>
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: `url(${gradientSrc})`,
              transform: 'translateZ(0)'
            }}
          />
          <div 
            className="absolute inset-0 bg-white dark:bg-gray-900"
            style={{ opacity: overlayOpacity }}
          />
        </>
      )}

      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default VideoBackground;
